import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Row, Col, Badge, Card } from 'react-bootstrap';

/**
 * 차트 타입별 사용 가능한 스타일 대상 정의
 */
const CHART_TYPE_TARGETS = {
  badge: ['title', 'value', 'unit'],
  // categoryLabel 제거 - 숨김 처리
  bar: ['title', 'legend', 'axis.x.title', 'axis.x.label', 'axis.y.title', 'axis.y.label', 'seriesLabel'],
  line: ['title', 'legend', 'axis.x.title', 'axis.x.label', 'axis.y.title', 'axis.y.label', 'seriesLabel'],
  pie: ['title', 'legend', 'seriesLabel'],
  scatter: ['title', 'legend', 'axis.x.title', 'axis.x.label', 'axis.y.title', 'axis.y.label', 'seriesLabel'],
  radar: ['title', 'legend', 'axis.x.title', 'axis.x.label', 'axis.y.title', 'axis.y.label', 'seriesLabel'],
  heatmap: ['title', 'legend', 'axis.x.title', 'axis.x.label', 'axis.y.title', 'axis.y.label', 'seriesLabel'],
  gauge: ['title', 'value', 'unit'],
  progress_bar: ['title', 'value', 'unit'],
  table: ['title', 'tableHeader', 'tableCell', 'tableRowHeader'], // 테이블 전용 스타일
  battery_grid: ['title', 'legend', 'batteryId', 'soc', 'soh'], // 배터리 그리드 차트 (state는 시각적 표현만)
  text_label: ['value'], // 텍스트라벨은 value만 사용
  date_picker: ['title', 'value', 'buttonText'] // DatePicker 전용 스타일
};

/**
 * 표시/숨김 가능한 요소 정의
 * 각 요소가 숨김 설정 가능한지 여부
 */
const HIDEABLE_TARGETS = {
  'title': true,           // 제목 - 숨김 가능
  'legend': true,          // 범례 - 숨김 가능
  'axis.x.title': true,    // X축 제목 - 숨김 가능
  'axis.y.title': true,    // Y축 제목 - 숨김 가능
  'axis.x.label': false,   // X축 라벨 - 숨김 불가 (축 자체의 값 표시)
  'axis.y.label': false,   // Y축 라벨 - 숨김 불가 (축 자체의 값 표시)
  'seriesLabel': true,     // 데이터 라벨 - 숨김 가능
  'categoryLabel': false,  // 카테고리 라벨 - 숨김 불가
  'value': false,          // 값 - 게이지/배지 등의 핵심 요소라 숨김 불가
  'unit': false,           // 단위 - 값과 연동되므로 숨김 불가
  'tableHeader': false,    // 테이블 헤더 - 구조상 필수
  'tableCell': false,      // 테이블 셀 - 구조상 필수
  'tableRowHeader': false,  // 테이블 행 헤더 - 구조상 필수
  'buttonText': true,      // DatePicker 버튼 텍스트 - 숨김 가능
  'batteryId': true,       // Battery Grid - 배터리 ID 표시
  'soc': true,             // Battery Grid - SOC 값 표시
  'soh': true              // Battery Grid - SOH 값 표시
};

/**
 * 스타일 대상의 한글 라벨 (차트 타입별로 다르게 표시)
 */
const getTargetLabel = (target, chartType) => {
  // 레이더차트의 경우 축 라벨을 다르게 표시
  if (chartType === 'radar') {
    const radarLabels = {
      'axis.x.title': '각도축 이름',
      'axis.x.label': '각도축 라벨',
      'axis.y.title': '반경축 제목', 
      'axis.y.label': '반경축 라벨'
    };
    if (radarLabels[target]) {
      return radarLabels[target];
    }
  }
  
  // Battery Grid의 경우 특별한 라벨 사용
  if (chartType === 'battery_grid') {
    const batteryGridLabels = {
      'batteryId': '배터리 ID',
      'soc': 'SOC 값',
      'soh': 'SOH 값'
    };
    if (batteryGridLabels[target]) {
      return batteryGridLabels[target];
    }
  }
  
  // DatePicker의 경우 특별한 라벨 사용
  if (chartType === 'date_picker') {
    const datePickerLabels = {
      'value': '입력 필드',
      'buttonText': '버튼 텍스트'
    };
    if (datePickerLabels[target]) {
      return datePickerLabels[target];
    }
  }
  
  // 기본 라벨
  const defaultLabels = {
    'title': '제목',
    'value': '값',
    'unit': '단위',
    'legend': '범례',
    'axis.x.title': 'X축 제목',
    'axis.x.label': 'X축 라벨',
    'axis.y.title': 'Y축 제목',
    'axis.y.label': 'Y축 라벨',
    'seriesLabel': '데이터 라벨',
    'categoryLabel': '카테고리',
    'tableHeader': '테이블 헤더',
    'tableCell': '테이블 셀',
    'tableRowHeader': '행 헤더',
    'buttonText': '버튼 텍스트'
  };
  
  return defaultLabels[target] || target;
};

/**
 * 기본 스타일 값 (하드코딩된 기본값)
 */
const DEFAULT_STYLES = {
  title: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 20,
    fontWeight: '600',
    fontStyle: 'normal',
    color: '#2c3e50',
    lineHeight: 1.4
  },
  legend: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#666666',
    lineHeight: 1.4
  },
  'axis.x.title': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 18,
    fontWeight: '600',
    fontStyle: 'normal',
    color: '#2c3e50',
    lineHeight: 1.4
  },
  'axis.x.label': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#666666',
    lineHeight: 1.4
  },
  'axis.y.title': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 18,
    fontWeight: '600',
    fontStyle: 'normal',
    color: '#2c3e50',
    lineHeight: 1.4
  },
  'axis.y.label': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#666666',
    lineHeight: 1.4
  },
  seriesLabel: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 11,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#333333',
    lineHeight: 1.4
  },
  categoryLabel: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#666666',
    lineHeight: 1.4
  },
  value: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 36,
    fontWeight: 'bold',
    fontStyle: 'normal',
    color: '#333333',
    lineHeight: 1.2
  },
  unit: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#666666',
    lineHeight: 1.4
  },
  tableHeader: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: '600',
    fontStyle: 'normal',
    color: '#2c3e50',
    lineHeight: 1.4
  },
  tableCell: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#333333',
    lineHeight: 1.4
  },
  tableRowHeader: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    fontWeight: '500',
    fontStyle: 'normal',
    color: '#495057',
    lineHeight: 1.4
  },
  buttonText: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#657786',
    lineHeight: 1.4
  }
};

/**
 * 공통 스타일 편집 패널 컴포넌트
 * @param {Object} props
 * @param {string} props.chartType - 차트 타입 (bar, line, pie, etc.)
 * @param {Object} props.style - 현재 스타일 설정 {fonts: {...}, background: {...}}
 * @param {Function} props.onChange - 스타일 변경 콜백
 * @param {boolean} props.showPreview - 미리보기 표시 여부
 */
const CommonStylePanel = ({
  chartType = 'bar',
  style = {},
  onChange,
  showPreview = false,
  axes = {},
  onAxesChange
}) => {
  // 사용 가능한 대상 목록
  const availableTargets = CHART_TYPE_TARGETS[chartType] || [];
  
  // 디버그 로그
  // 현재 선택된 대상 (단일 선택) - null로 시작하고 useEffect에서 초기화
  const [selectedTarget, setSelectedTarget] = useState(null);
  
  // 변경사항 즉시 저장을 위한 디바운스 타이머
  const saveTimerRef = useRef(null);
  
  // 이전 style 값 저장 (깊은 비교용)
  const prevStyleRef = useRef(null);
  
  // 이전 selectedTarget 저장
  const prevSelectedTargetRef = useRef(null);
  
  // 사용자가 편집 중일 때 외부 style 변경 무시를 위한 타이머
  const ignoreExternalStyleChangeRef = useRef(false);
  
  // 폰트 설정 (현재 값과 편집 값 통합)
  const [fontSettings, setFontSettings] = useState({
    fontFamily: '',
    fontSize: '',
    fontWeight: '',
    fontStyle: '',
    color: '',
    lineHeight: ''
  });
  
  // 각 필드별로 템플릿에 값이 있는지 추적
  const [fieldStatus, setFieldStatus] = useState({
    fontFamily: false,
    fontSize: false,
    fontWeight: false,
    fontStyle: false,
    color: false,
    lineHeight: false
  });
  
  // 각 필드의 출처 추적 (standard, legacy, default)
  const [fieldSources, setFieldSources] = useState({});
  
  // 레거시 값 존재 여부
  const [hasLegacyValues, setHasLegacyValues] = useState(false);
  
  // 현재 대상의 값이 템플릿에 설정되어 있는지 여부
  const [isValueSet, setIsValueSet] = useState(false);
  
  // 사용자가 실제로 값을 변경했는지 추적
  const [hasUserModified, setHasUserModified] = useState(false);
  
  
  // 기존 스타일에서 특정 대상의 값 가져오기
  const getTargetValue = useCallback((target) => {
    if (!style?.fonts) return null;
    
    if (target.includes('axis.')) {
      // axis.x.title, axis.y.label 등 처리
      const [, axis, type] = target.split('.');
      return style.fonts.axis?.[axis]?.[type];
    } else {
      return style.fonts[target];
    }
  }, [style]);
  
  // 대상 선택 시 현재 값 로드
  const selectTarget = useCallback((target) => {
    // 이전 대상의 변경사항을 자동 저장 (사용자가 수정한 경우에만)
    if (selectedTarget && selectedTarget !== target && hasUserModified) {
      // 현재 폰트 설정을 저장
      const newStyle = { ...style };
      if (!newStyle.fonts) newStyle.fonts = {};
      
      // axis 타입 처리
      if (selectedTarget.includes('axis.')) {
        const [, axis, type] = selectedTarget.split('.');
        if (!newStyle.fonts.axis) newStyle.fonts.axis = {};
        if (!newStyle.fonts.axis[axis]) newStyle.fonts.axis[axis] = {};
        
        // 빈 값이 아닌 경우에만 저장
        const styleToSave = {};
        if (fontSettings.fontFamily && fontSettings.fontFamily !== '') styleToSave.fontFamily = fontSettings.fontFamily;
        if (fontSettings.fontSize && fontSettings.fontSize !== '') styleToSave.fontSize = fontSettings.fontSize;
        if (fontSettings.fontWeight && fontSettings.fontWeight !== '') styleToSave.fontWeight = fontSettings.fontWeight;
        if (fontSettings.fontStyle && fontSettings.fontStyle !== '') styleToSave.fontStyle = fontSettings.fontStyle;
        if (fontSettings.color && fontSettings.color !== '') styleToSave.color = fontSettings.color;
        if (fontSettings.lineHeight && fontSettings.lineHeight !== '') styleToSave.lineHeight = fontSettings.lineHeight;
        
        if (Object.keys(styleToSave).length > 0) {
          newStyle.fonts.axis[axis][type] = styleToSave;
        }
      } else {
        // 일반 대상 (title, legend, value 등)
        const styleToSave = {};
        if (fontSettings.fontFamily && fontSettings.fontFamily !== '') styleToSave.fontFamily = fontSettings.fontFamily;
        if (fontSettings.fontSize && fontSettings.fontSize !== '') styleToSave.fontSize = fontSettings.fontSize;
        if (fontSettings.fontWeight && fontSettings.fontWeight !== '') styleToSave.fontWeight = fontSettings.fontWeight;
        if (fontSettings.fontStyle && fontSettings.fontStyle !== '') styleToSave.fontStyle = fontSettings.fontStyle;
        if (fontSettings.color && fontSettings.color !== '') styleToSave.color = fontSettings.color;
        if (fontSettings.lineHeight && fontSettings.lineHeight !== '') styleToSave.lineHeight = fontSettings.lineHeight;
        
        if (Object.keys(styleToSave).length > 0) {
          newStyle.fonts[selectedTarget] = styleToSave;
        }
      }
      
      // 변경사항 전달
      onChange(newStyle);
    }
    
    // 새로운 대상 선택
    setSelectedTarget(target);
    // 대상 변경 시 수정 플래그 리셋
    setHasUserModified(false);
    
    // 템플릿에서 값 가져오기
    const savedValue = getTargetValue(target);
    const defaultStyle = DEFAULT_STYLES[target] || DEFAULT_STYLES.title;
    
    // 배지나 게이지 차트의 경우 advanced에서도 값 확인
    let advancedValue = null;
    if (chartType === 'badge' || chartType === 'gauge' || chartType === 'progress_bar') {
      const advanced = style?.advanced || {};
      if (target === 'title' && advanced.titleFontSize) {
        advancedValue = {
          fontSize: advanced.titleFontSize,
          color: advanced.titleColor || null,
          fontWeight: advanced.titleFontWeight || null,
          fontFamily: null,
          fontStyle: null,
          lineHeight: null
        };
      } else if (target === 'value' && advanced.fontSize) {
        advancedValue = {
          fontSize: advanced.fontSize,
          color: advanced.textColor || null,
          fontWeight: advanced.fontWeight || null,
          fontFamily: null,
          fontStyle: null,
          lineHeight: null
        };
      } else if (target === 'unit' && advanced.unitFontSize) {
        advancedValue = {
          fontSize: advanced.unitFontSize,
          color: advanced.unitColor || advanced.textColor || null,
          fontWeight: advanced.unitFontWeight || null,
          fontFamily: null,
          fontStyle: null,
          lineHeight: null
        };
      }
    }
    
    // 우선순위: style.fonts > advanced > 기본값
    const effectiveValue = savedValue || advancedValue;
    
    // 각 필드별로 개별적으로 체크
    const finalSettings = {};
    const newFieldStatus = {};
    const newFieldSources = {}; // 각 필드의 출처 추적
    let anyLegacyValue = false;
    
    ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'color', 'lineHeight'].forEach(field => {
      // style.fonts에서 먼저 체크
      let hasValue = false;
      let value = null;
      let source = 'default';
      
      if (savedValue && savedValue[field] !== undefined && savedValue[field] !== null) {
        // style.fonts에 값이 있음 (표준)
        value = savedValue[field];
        hasValue = true;
        source = 'standard';
      } else if (advancedValue && advancedValue[field] !== undefined && advancedValue[field] !== null) {
        // advanced에 값이 있음 (레거시)
        value = advancedValue[field];
        hasValue = true;
        source = 'legacy';
        anyLegacyValue = true;
      }
      
      if (hasValue) {
        finalSettings[field] = value;
        newFieldStatus[field] = true;
        newFieldSources[field] = source;
      } else {
        // 템플릿에 없으면 기본값 표시를 위해 필드별로 처리
        if (field === 'fontWeight' || field === 'fontStyle') {
          // Select 필드: 빈 문자열로 '-' 표시
          finalSettings[field] = '';
        } else if (field === 'fontSize' || field === 'lineHeight') {
          // 숫자 필드: 빈 문자열 또는 null로 설정하여 placeholder 표시
          finalSettings[field] = '';
        } else if (field === 'fontFamily') {
          // 폰트 패밀리: 기본값 표시 (선택 목록에서 하나는 선택되어야 함)
          finalSettings[field] = defaultStyle[field];
        } else if (field === 'color') {
          // 색상: 값이 없으면 '-' 표기를 위해 빈 문자열 유지
          // (실제 컬러 피커 표시값은 렌더링 시 기본값으로 대체)
          finalSettings[field] = '';
        } else {
          finalSettings[field] = defaultStyle[field];
        }
        newFieldStatus[field] = false;
        newFieldSources[field] = 'default';
      }
    });
    
    // style prop이 실제로 변경되었는지 깊은 비교
    const styleKey = JSON.stringify(style);
    const prevStyleKey = prevStyleRef.current;
    const prevSelectedTarget = prevSelectedTargetRef.current;
    
    // selectedTarget이 변경되었는지 먼저 체크
    const targetChanged = prevSelectedTarget !== selectedTarget;
    const styleChanged = prevStyleKey !== styleKey;
    
    // 대상이 변경된 경우는 무조건 업데이트 (ignoreFlag 무시)
    // 대상이 동일하고 사용자가 편집 중일 때만 외부 style 변경을 무시
    if (!targetChanged && ignoreExternalStyleChangeRef.current) {
      return;
    }
    
    // selectedTarget이 변경되었거나, style의 실제 내용이 변경된 경우에만 업데이트
    if (targetChanged || styleChanged || !prevStyleRef.current) {
      
      prevStyleRef.current = styleKey;
      prevSelectedTargetRef.current = selectedTarget;
      
      setFontSettings(finalSettings);
      setFieldStatus(newFieldStatus);
      setFieldSources(newFieldSources);
      // style.fonts에 값이 있을 때만 "설정됨"으로 표시
      setIsValueSet(!!savedValue);
      // 레거시 값만 있는 경우 추적
      setHasLegacyValues(anyLegacyValue && !savedValue);
    }
  }, [chartType, style, getTargetValue, selectedTarget]);
  

  // 폰트 설정 변경 시 자동 저장 (디바운스 적용)
  useEffect(() => {
    if (!selectedTarget) {
      return;
    }
    
    // 사용자가 실제로 값을 변경하지 않았으면 저장하지 않음
    if (!hasUserModified) {
      return;
    }
    
    // 사용자가 편집 중임을 표시 (외부 style 변경 무시)
    ignoreExternalStyleChangeRef.current = true;
    
    // 이전 타이머 취소
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // 500ms 후 자동 저장
    saveTimerRef.current = setTimeout(() => {
      const newStyle = { ...style };
      if (!newStyle.fonts) newStyle.fonts = {};
      
      // axis 타입 처리
      if (selectedTarget.includes('axis.')) {
        const [, axis, type] = selectedTarget.split('.');
        if (!newStyle.fonts.axis) newStyle.fonts.axis = {};
        if (!newStyle.fonts.axis[axis]) newStyle.fonts.axis[axis] = {};
        
        // 빈 값이 아닌 경우에만 저장
        const styleToSave = {};
        if (fontSettings.fontFamily && fontSettings.fontFamily !== '') styleToSave.fontFamily = fontSettings.fontFamily;
        if (fontSettings.fontSize && fontSettings.fontSize !== '') styleToSave.fontSize = fontSettings.fontSize;
        if (fontSettings.fontWeight && fontSettings.fontWeight !== '') styleToSave.fontWeight = fontSettings.fontWeight;
        if (fontSettings.fontStyle && fontSettings.fontStyle !== '') styleToSave.fontStyle = fontSettings.fontStyle;
        if (fontSettings.color && fontSettings.color !== '') styleToSave.color = fontSettings.color;
        if (fontSettings.lineHeight && fontSettings.lineHeight !== '') styleToSave.lineHeight = fontSettings.lineHeight;
        
        if (Object.keys(styleToSave).length > 0) {
          newStyle.fonts.axis[axis][type] = styleToSave;
        }
      } else {
        // 일반 대상 (title, legend, value 등)
        const styleToSave = {};
        if (fontSettings.fontFamily && fontSettings.fontFamily !== '') styleToSave.fontFamily = fontSettings.fontFamily;
        if (fontSettings.fontSize && fontSettings.fontSize !== '') styleToSave.fontSize = fontSettings.fontSize;
        if (fontSettings.fontWeight && fontSettings.fontWeight !== '') styleToSave.fontWeight = fontSettings.fontWeight;
        if (fontSettings.fontStyle && fontSettings.fontStyle !== '') styleToSave.fontStyle = fontSettings.fontStyle;
        if (fontSettings.color && fontSettings.color !== '') styleToSave.color = fontSettings.color;
        if (fontSettings.lineHeight && fontSettings.lineHeight !== '') styleToSave.lineHeight = fontSettings.lineHeight;
        
        if (Object.keys(styleToSave).length > 0) {
          newStyle.fonts[selectedTarget] = styleToSave;
        }
      }
      
      // 변경사항 전달
      onChange(newStyle);
      setIsValueSet(true);
      // 저장 후 수정 플래그 리셋
      setHasUserModified(false);
      
      // onChange 호출 후 짧은 지연 후 외부 style 변경 허용
      setTimeout(() => {
        ignoreExternalStyleChangeRef.current = false;
      }, 100);
    }, 500);
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [fontSettings, selectedTarget, hasUserModified]);
  
  // 첫 번째 대상 자동 선택 (컴포넌트 마운트 시)
  useEffect(() => {
    if (availableTargets.length > 0 && !selectedTarget) {
      // 텍스트라벨은 기본적으로 value 선택
      if (chartType === 'text_label' && availableTargets.includes('value')) {
        selectTarget('value');
      }
      // 배지나 게이지는 기본적으로 title 선택
      else if ((chartType === 'badge' || chartType === 'gauge' || chartType === 'progress_bar') && availableTargets.includes('title')) {
        selectTarget('title');
      }
      // 다른 차트는 기본적으로 첫 번째 대상 선택
      else if (availableTargets[0]) {
        selectTarget(availableTargets[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType]); // chartType 변경 시에만 재실행

  // 부모에서 전달된 style이 갱신될 때 현재 대상 값을 다시 로드하여 기본값이 덮어쓰는 문제 방지
  useEffect(() => {
    if (!availableTargets.length) return;
    
    // 사용자가 편집 중일 때는 외부 style 변경으로 인한 리로드를 건너뜀
    if (hasUserModified) {
      return;
    }

    const getDefaultTarget = () => {
      if (chartType === 'text_label' && availableTargets.includes('value')) return 'value';
      if ((chartType === 'badge' || chartType === 'gauge' || chartType === 'progress_bar') && availableTargets.includes('title')) return 'title';
      return availableTargets[0];
    };

    // 현재 선택된 대상이 있으면 그것을, 없으면 기본 대상을 로드
    const targetToReload = selectedTarget || getDefaultTarget();
    if (targetToReload) {
      selectTarget(targetToReload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style]);
  
  // 각 요소의 표시/숨김 상태 관리
  const getElementVisibility = useCallback((target) => {
    // 범례의 경우 하위 호환성 처리
    if (target === 'legend') {
      // visibility.legend이 정의되어 있으면 그것을 사용
      if (style?.visibility?.legend !== undefined) {
        return style.visibility.legend !== false;
      }
      // 기존 options.showLegend이 있으면 그것을 사용 (마이그레이션 필요)
      if (style?.options?.showLegend !== undefined) {
        return style.options.showLegend;
      }
      // 둘 다 없으면 기본값 true
      return true;
    }
    
    if (!style?.visibility) return true; // 기본값: 표시
    
    if (target.includes('axis.')) {
      const [, axis, type] = target.split('.');
      return style.visibility.axis?.[axis]?.[type] !== false;
    } else {
      return style.visibility[target] !== false;
    }
  }, [style]);
  
  // 요소 표시/숨김 토글 처리
  const handleVisibilityToggle = useCallback((target, visible) => {
    const newStyle = { ...style };
    if (!newStyle.visibility) newStyle.visibility = {};
    
    if (target.includes('axis.')) {
      const [, axis, type] = target.split('.');
      if (!newStyle.visibility.axis) newStyle.visibility.axis = {};
      if (!newStyle.visibility.axis[axis]) newStyle.visibility.axis[axis] = {};
      newStyle.visibility.axis[axis][type] = visible;
    } else {
      newStyle.visibility[target] = visible;
      
      // 범례의 경우 기존 options.showLegend 제거 (마이그레이션 완료)
      if (target === 'legend' && newStyle.options?.showLegend !== undefined) {
        delete newStyle.options.showLegend;
        // options가 빈 객체가 되면 제거
        if (Object.keys(newStyle.options).length === 0) {
          delete newStyle.options;
        }
      }
    }
    
    onChange(newStyle);
  }, [style, onChange]);

  return (
    <div className="common-style-panel">
      {/* 대상 선택 섹션 */}
      <Card className="mb-3">
        <Card.Header>
          <span>스타일 편집 대상</span>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-wrap gap-2">
            {availableTargets.map(target => (
              <Badge
                key={target}
                bg={selectedTarget === target ? 'primary' : 'secondary'}
                style={{ 
                  cursor: 'pointer', 
                  fontSize: '0.875rem',
                  padding: '0.5rem 0.75rem'
                }}
                onClick={() => selectTarget(target)}
              >
                {getTargetLabel(target, chartType)}
                {selectedTarget === target && ' ✓'}
              </Badge>
            ))}
          </div>
          {!selectedTarget && (
            <div className="text-muted mt-2">
              <small>편집할 대상을 선택해주세요.</small>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* 폰트 설정 섹션 (통합된 UI) */}
      {selectedTarget && (
        <Card className="mb-3">
          <Card.Header className="d-flex align-items-center justify-content-between">
            <div>
              {getTargetLabel(selectedTarget, chartType)} 스타일 설정
              {!isValueSet && hasLegacyValues && (
                <Badge bg="warning" className="ms-2" style={{ fontSize: '0.75rem' }}>
                  레거시 형식
                </Badge>
              )}
              {!isValueSet && !hasLegacyValues && (
                <Badge bg="info" className="ms-2" style={{ fontSize: '0.75rem' }}>
                  기본값 사용 중
                </Badge>
              )}
            </div>
            {/* 숨김 가능한 요소에 대해 토글 스위치 표시 */}
            {HIDEABLE_TARGETS[selectedTarget] && (
              <div className="d-flex align-items-center">
                <span className="me-2" style={{ fontSize: '0.875rem' }}>표시</span>
                <Form.Check
                  type="switch"
                  id={`visibility-toggle-${selectedTarget}`}
                  checked={getElementVisibility(selectedTarget)}
                  onChange={(e) => handleVisibilityToggle(selectedTarget, e.target.checked)}
                  style={{ marginBottom: 0 }}
                />
              </div>
            )}
          </Card.Header>
          <Card.Body>
            {!isValueSet && hasLegacyValues && (
              <div className="alert alert-warning mb-3" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-exclamation-triangle"></i> 
                <strong>레거시 형식 감지:</strong> advanced 속성에서 값을 불러왔습니다. 
                저장 시 표준 형식(style.fonts)으로 자동 변환되며, 레거시 속성은 제거됩니다.
              </div>
            )}
            {!isValueSet && !hasLegacyValues && (
              <div className="alert alert-info mb-3" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-info-circle"></i>
                현재 템플릿에 설정된 값이 없어 기본값을 표시합니다. 변경 후 적용하면 템플릿에 저장됩니다.
              </div>
            )}

            {/* 축 제목 텍스트 입력 (X축/Y축 제목 선택 시) */}
            {onAxesChange && (selectedTarget === 'axis.x.title' || selectedTarget === 'axis.y.title') && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      {selectedTarget === 'axis.x.title' ? 'X축 제목 텍스트' : 'Y축 제목 텍스트'}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={selectedTarget === 'axis.x.title' ? 'X축 제목을 입력하세요' : 'Y축 제목을 입력하세요'}
                      value={(selectedTarget === 'axis.x.title' ? axes?.x : axes?.y) || ''}
                      onChange={(e) => {
                        const axisKey = selectedTarget === 'axis.x.title' ? 'x' : 'y';
                        onAxesChange({ ...axes, [axisKey]: e.target.value });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    폰트 패밀리
                    {fieldSources.fontFamily === 'legacy' && <span className="text-warning ms-1" style={{ fontSize: '0.8rem' }}>(레거시)</span>}
                    {fieldSources.fontFamily === 'default' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(기본값)</span>}
                  </Form.Label>
                  <Form.Select
                    value={fontSettings.fontFamily}
                    onChange={(e) => {
                      setFontSettings({...fontSettings, fontFamily: e.target.value});
                      setHasUserModified(true);
                    }}
                    style={{ 
                      backgroundColor: !fieldStatus.fontFamily ? '#f8f9fa' : 'white',
                      borderColor: !fieldStatus.fontFamily ? '#dee2e6' : '#ced4da'
                    }}
                  >
                    <option value="Inter, sans-serif">Inter</option>
                    <option value="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif">System UI</option>
                    <option value="'Noto Sans KR', sans-serif">Noto Sans KR</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    폰트 크기
                    {fieldSources.fontSize === 'legacy' && <span className="text-warning ms-1" style={{ fontSize: '0.8rem' }}>(레거시)</span>}
                    {fieldSources.fontSize === 'default' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(기본값)</span>}
                  </Form.Label>
                  <Form.Select
                    value={!fieldStatus.fontSize ? '' : fontSettings.fontSize}
                    onChange={(e) => {
                      if (e.target.value) {
                        setFontSettings({...fontSettings, fontSize: parseInt(e.target.value)});
                        setFieldStatus({...fieldStatus, fontSize: true});
                        setHasUserModified(true);
                      }
                    }}
                    style={{ 
                      backgroundColor: !fieldStatus.fontSize ? '#f8f9fa' : 'white',
                      borderColor: !fieldStatus.fontSize ? '#dee2e6' : '#ced4da'
                    }}
                  >
                    {!fieldStatus.fontSize && <option value="">-</option>}
                    {/* 현재 값이 목록에 없는 커스텀 값인 경우 추가 */}
                    {fieldStatus.fontSize && fontSettings.fontSize && 
                     ![8,9,10,11,12,14,16,18,20,24,28,32,36,40,48,56,64,72,80,88,96,112,128].includes(fontSettings.fontSize) && (
                      <option value={fontSettings.fontSize}>{fontSettings.fontSize}px (커스텀)</option>
                    )}
                    <option value="8">8px</option>
                    <option value="9">9px</option>
                    <option value="10">10px</option>
                    <option value="11">11px</option>
                    <option value="12">12px</option>
                    <option value="14">14px</option>
                    <option value="16">16px</option>
                    <option value="18">18px</option>
                    <option value="20">20px</option>
                    <option value="24">24px</option>
                    <option value="28">28px</option>
                    <option value="32">32px</option>
                    <option value="36">36px</option>
                    <option value="40">40px</option>
                    <option value="48">48px</option>
                    <option value="56">56px</option>
                    <option value="64">64px</option>
                    <option value="72">72px</option>
                    <option value="80">80px</option>
                    <option value="88">88px</option>
                    <option value="96">96px</option>
                    <option value="112">112px</option>
                    <option value="128">128px</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {fieldStatus.fontSize ? `선택된 크기: ${fontSettings.fontSize}px` : `기본값: ${DEFAULT_STYLES[selectedTarget]?.fontSize || 14}px`}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    폰트 굵기
                    {fieldSources.fontWeight === 'legacy' && <span className="text-warning ms-1" style={{ fontSize: '0.8rem' }}>(레거시)</span>}
                    {fieldSources.fontWeight === 'default' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(기본값)</span>}
                  </Form.Label>
                  <Form.Select
                    value={!fieldStatus.fontWeight ? '' : fontSettings.fontWeight}
                    onChange={(e) => {
                      if (e.target.value) {
                        setFontSettings({...fontSettings, fontWeight: e.target.value});
                        setFieldStatus({...fieldStatus, fontWeight: true});
                        setHasUserModified(true);
                      }
                    }}
                    style={{ 
                      backgroundColor: !fieldStatus.fontWeight ? '#f8f9fa' : 'white',
                      borderColor: !fieldStatus.fontWeight ? '#dee2e6' : '#ced4da'
                    }}
                  >
                    {!fieldStatus.fontWeight && <option value="">-</option>}
                    <option value="100">Thin (100)</option>
                    <option value="200">Extra Light (200)</option>
                    <option value="300">Light (300)</option>
                    <option value="normal">Normal (400)</option>
                    <option value="500">Medium (500)</option>
                    <option value="600">Semi Bold (600)</option>
                    <option value="bold">Bold (700)</option>
                    <option value="800">Extra Bold (800)</option>
                    <option value="900">Black (900)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    폰트 스타일
                    {fieldSources.fontStyle === 'legacy' && <span className="text-warning ms-1" style={{ fontSize: '0.8rem' }}>(레거시)</span>}
                    {fieldSources.fontStyle === 'default' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(기본값)</span>}
                  </Form.Label>
                  <Form.Select
                    value={!fieldStatus.fontStyle ? '' : fontSettings.fontStyle}
                    onChange={(e) => {
                      if (e.target.value) {
                        setFontSettings({...fontSettings, fontStyle: e.target.value});
                        setFieldStatus({...fieldStatus, fontStyle: true});
                        setHasUserModified(true);
                      }
                    }}
                    style={{ 
                      backgroundColor: !fieldStatus.fontStyle ? '#f8f9fa' : 'white',
                      borderColor: !fieldStatus.fontStyle ? '#dee2e6' : '#ced4da'
                    }}
                  >
                    {!fieldStatus.fontStyle && <option value="">-</option>}
                    <option value="normal">일반</option>
                    <option value="italic">이탤릭</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    텍스트 색상
                    {fieldSources.color === 'legacy' && <span className="text-warning ms-1" style={{ fontSize: '0.8rem' }}>(레거시)</span>}
                    {fieldSources.color === 'default' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(기본값)</span>}
                  </Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={fontSettings.color && fontSettings.color !== '' ? fontSettings.color : (DEFAULT_STYLES[selectedTarget]?.color || '#333333')}
                      onChange={(e) => {
                        setFontSettings({...fontSettings, color: e.target.value});
                        setFieldStatus({...fieldStatus, color: true});
                        setHasUserModified(true);
                      }}
                      style={{ 
                        width: '60px',
                        backgroundColor: !isValueSet ? '#f8f9fa' : 'white',
                        borderColor: !isValueSet ? '#dee2e6' : '#ced4da'
                      }}
                    />
                    <Form.Control
                      type="text"
                      value={fontSettings.color}
                      onChange={(e) => {
                        setFontSettings({...fontSettings, color: e.target.value});
                        setFieldStatus({...fieldStatus, color: true});
                        setHasUserModified(true);
                      }}
                      placeholder="-"
                      style={{ 
                        flex: 1,
                        backgroundColor: !isValueSet ? '#f8f9fa' : 'white',
                        borderColor: !isValueSet ? '#dee2e6' : '#ced4da'
                      }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    줄 간격
                    {fieldSources.lineHeight === 'legacy' && <span className="text-warning ms-1" style={{ fontSize: '0.8rem' }}>(레거시)</span>}
                    {fieldSources.lineHeight === 'default' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(기본값)</span>}
                  </Form.Label>
                  <Form.Select
                    value={fontSettings.lineHeight || ''}
                    onChange={(e) => {
                      setFontSettings({...fontSettings, lineHeight: e.target.value ? parseFloat(e.target.value) : ''});
                      setHasUserModified(true);
                    }}
                    style={{ 
                      backgroundColor: !fieldStatus.lineHeight ? '#f8f9fa' : 'white',
                      borderColor: !fieldStatus.lineHeight ? '#dee2e6' : '#ced4da'
                    }}
                  >
                    {!fieldStatus.lineHeight && <option value="">-</option>}
                    <option value="1.0">좁게 (1.0)</option>
                    <option value="1.2">보통 좁게 (1.2)</option>
                    <option value="1.4">보통 (1.4)</option>
                    <option value="1.6">보통 넓게 (1.6)</option>
                    <option value="1.8">넓게 (1.8)</option>
                    <option value="2.0">매우 넓게 (2.0)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      {/* 파이 차트 전용 옵션 섹션 */}
      {chartType === 'pie' && (
        <Card className="mb-3">
          <Card.Header>파이차트 옵션</Card.Header>
          <Card.Body>
            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>내부 구멍 크기</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Range
                      min="0"
                      max="60"
                      value={style?.innerRadius ?? 40}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        newStyle.innerRadius = parseInt(e.target.value);
                        onChange(newStyle);
                      }}
                      className="flex-grow-1"
                    />
                    <span style={{ minWidth: '50px' }}>
                      {style?.innerRadius ?? 40}%
                    </span>
                  </div>
                  <Form.Text className="text-muted">
                    파이차트 중앙의 구멍 크기를 조절합니다 (0: 원형, 60: 최대 도넛)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* 레이더 차트 전용 옵션 섹션 */}
      {chartType === 'radar' && (
        <Card className="mb-3">
          <Card.Header>레이더차트 정규화 옵션</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="manual-normalization"
                  label="수동 정규화 사용"
                  checked={style?.advanced?.manualNormalization === true}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    newStyle.advanced.manualNormalization = e.target.checked;
                    // 수동 정규화를 끄면 min/max 값도 초기화
                    if (!e.target.checked) {
                      delete newStyle.advanced.normalizationMin;
                      delete newStyle.advanced.normalizationMax;
                    }
                    onChange(newStyle);
                  }}
                />
                <Form.Text className="text-muted">
                  기본값: 자동 정규화 (데이터의 최소/최대값을 0-100으로 자동 변환)
                </Form.Text>
              </Col>
            </Row>

            {style?.advanced?.manualNormalization === true && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>최소값 (Min)</Form.Label>
                      <Form.Control
                        type="number"
                        value={style?.advanced?.normalizationMin ?? 0}
                        onChange={(e) => {
                          const newStyle = { ...style };
                          if (!newStyle.advanced) newStyle.advanced = {};
                          newStyle.advanced.normalizationMin = parseFloat(e.target.value) || 0;
                          onChange(newStyle);
                        }}
                        placeholder="0"
                      />
                      <Form.Text className="text-muted">
                        데이터의 최소값으로 간주할 값
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>최대값 (Max)</Form.Label>
                      <Form.Control
                        type="number"
                        value={style?.advanced?.normalizationMax ?? 100}
                        onChange={(e) => {
                          const newStyle = { ...style };
                          if (!newStyle.advanced) newStyle.advanced = {};
                          newStyle.advanced.normalizationMax = parseFloat(e.target.value) || 100;
                          onChange(newStyle);
                        }}
                        placeholder="100"
                      />
                      <Form.Text className="text-muted">
                        데이터의 최대값으로 간주할 값
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="alert alert-info" style={{ fontSize: '0.875rem' }}>
                      <i className="bi bi-info-circle"></i> <strong>수동 정규화 사용법:</strong><br/>
                      • 예시 1: 온도 데이터 (-20°C ~ 50°C) → Min: -20, Max: 50<br/>
                      • 예시 2: 백분율 데이터 (0% ~ 100%) → Min: 0, Max: 100<br/>
                      • 예시 3: 점수 데이터 (0 ~ 5점) → Min: 0, Max: 5<br/>
                      <br/>
                      설정한 Min/Max 범위를 기준으로 모든 데이터가 0-100 범위로 정규화되어 레이더 차트에 표시됩니다.
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      )}
      
      {/* 데이터 라벨 상시 표시 옵션 (카테시안/파이/레이더/산점도 공통) */}
      {['bar','line','scatter','heatmap','radar','pie'].includes(chartType) && (
        <Card className="mb-3">
          <Card.Header>데이터 라벨</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="show-data-labels"
                  label="데이터 라벨 상시 표시"
                  checked={style?.advanced?.showDataLabels === true}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    newStyle.advanced.showDataLabels = e.target.checked;
                    onChange(newStyle);
                  }}
                />
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>라벨 포맷</Form.Label>
                  <Form.Control
                    type="text"
                    value="기본 ({value})"
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>라벨 위치</Form.Label>
                  <Form.Select
                    value={style?.advanced?.labelPosition || ''}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.labelPosition = e.target.value || undefined;
                      onChange(newStyle);
                    }}
                    disabled={style?.advanced?.showDataLabels !== true}
                  >
                    <option value="">기본</option>
                    <option value="top">상단</option>
                    <option value="inside">내부</option>
                    {chartType === 'pie' && <option value="outside">외부</option>}
                    <option value="right">오른쪽</option>
                    <option value="left">왼쪽</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* 공통 레이아웃(그리드 여백) 섹션: ECharts 카테시안 기반 차트에 적용 */}
      {['bar','line','scatter','heatmap','radar','pie'].includes(chartType) && (
        <Card className="mb-3">
          <Card.Header>레이아웃 (그리드 여백)</Card.Header>
          <Card.Body>
            <div className="mb-2" style={{ fontSize: '0.875rem', color: '#6c757d' }}>
              grid는 차트 컨테이너 기준으로 상/하/좌/우에 두는 여백 비율(%)입니다. 0%는 모서리에 밀착, 값이 클수록 여백이 커집니다.
            </div>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Top</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => {
                        const v = style?.advanced?.grid?.top;
                        if (v === undefined || v === null || v === '') return '';
                        if (typeof v === 'number') return v;
                        return parseFloat(String(v).replace('%','')) || 0;
                      })()}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.advanced) newStyle.advanced = {};
                        if (!newStyle.advanced.grid) newStyle.advanced.grid = {};
                        const num = Number(e.target.value);
                        newStyle.advanced.grid.top = isNaN(num) ? '' : `${Math.max(0, Math.min(100, num))}%`;
                        onChange(newStyle);
                      }}
                      placeholder="예: 6"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Bottom</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => {
                        const v = style?.advanced?.grid?.bottom;
                        if (v === undefined || v === null || v === '') return '';
                        if (typeof v === 'number') return v;
                        return parseFloat(String(v).replace('%','')) || 0;
                      })()}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.advanced) newStyle.advanced = {};
                        if (!newStyle.advanced.grid) newStyle.advanced.grid = {};
                        const num = Number(e.target.value);
                        newStyle.advanced.grid.bottom = isNaN(num) ? '' : `${Math.max(0, Math.min(100, num))}%`;
                        onChange(newStyle);
                      }}
                      placeholder="예: 8"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Left</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => {
                        const v = style?.advanced?.grid?.left;
                        if (v === undefined || v === null || v === '') return '';
                        if (typeof v === 'number') return v;
                        return parseFloat(String(v).replace('%','')) || 0;
                      })()}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.advanced) newStyle.advanced = {};
                        if (!newStyle.advanced.grid) newStyle.advanced.grid = {};
                        const num = Number(e.target.value);
                        newStyle.advanced.grid.left = isNaN(num) ? '' : `${Math.max(0, Math.min(100, num))}%`;
                        onChange(newStyle);
                      }}
                      placeholder="예: 6"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Right</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => {
                        const v = style?.advanced?.grid?.right;
                        if (v === undefined || v === null || v === '') return '';
                        if (typeof v === 'number') return v;
                        return parseFloat(String(v).replace('%','')) || 0;
                      })()}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.advanced) newStyle.advanced = {};
                        if (!newStyle.advanced.grid) newStyle.advanced.grid = {};
                        const num = Number(e.target.value);
                        newStyle.advanced.grid.right = isNaN(num) ? '' : `${Math.max(0, Math.min(100, num))}%`;
                        onChange(newStyle);
                      }}
                      placeholder="예: 4"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            {/* 라벨 잘림 방지 옵션 - 숨김 처리 */}
            {/* <Form.Group>
              <Form.Check
                type="switch"
                id="grid-contain-label"
                label="라벨 잘림 방지(containLabel)"
                checked={style?.advanced?.grid?.containLabel === true}
                onChange={(e) => {
                  const newStyle = { ...style };
                  if (!newStyle.advanced) newStyle.advanced = {};
                  if (!newStyle.advanced.grid) newStyle.advanced.grid = {};
                  newStyle.advanced.grid.containLabel = e.target.checked;
                  onChange(newStyle);
                }}
              />
              <Form.Text className="text-muted">
                기본값은 꺼짐(false)입니다. 켜면 축 라벨이 잘리지 않도록 여백이 자동으로 확보될 수 있습니다.
              </Form.Text>
            </Form.Group> */}
          </Card.Body>
        </Card>
      )}
      
      {/* 프로그레스바 전용 옵션 섹션은 NLComponentModal에서 통합 관리합니다. */}
      
      {/* DatePicker 전용 옵션 섹션 */}
      {chartType === 'date_picker' && (
        <Card className="mb-3">
          <Card.Header>DatePicker 옵션</Card.Header>
          <Card.Body>
            <Row>
              <Col md={12}>
                {/* 시간 표시 옵션 */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="date-picker-show-time"
                    label="시간 선택 표시"
                    checked={style?.advanced?.showTime === true}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.showTime = e.target.checked;
                      onChange(newStyle);
                    }}
                  />
                  <Form.Text className="text-muted">
                    날짜와 함께 시간 선택 기능을 활성화합니다.
                  </Form.Text>
                </Form.Group>

                {/* 입력 필드 너비 조절 */}
                <Form.Group className="mb-3">
                  <Form.Label>입력 필드 너비</Form.Label>
                  
                  {/* Auto 너비 토글 */}
                  <Form.Check
                    type="switch"
                    id="date-picker-auto-width"
                    label="자동 너비 조정"
                    className="mb-2"
                    checked={style?.advanced?.inputFieldWidth === 'auto' || !style?.advanced?.inputFieldWidth}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.inputFieldWidth = e.target.checked ? 'auto' : '50%';
                      onChange(newStyle);
                    }}
                  />
                  
                  {/* 수동 너비 조정 슬라이더 */}
                  {style?.advanced?.inputFieldWidth && style?.advanced?.inputFieldWidth !== 'auto' && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small>너비:</small>
                        <Badge bg="secondary">
                          {parseInt(style?.advanced?.inputFieldWidth) || 50}%
                        </Badge>
                      </div>
                      <Form.Range
                        min="20"
                        max="100"
                        step="5"
                        value={parseInt(style?.advanced?.inputFieldWidth) || 50}
                        onChange={(e) => {
                          const newStyle = { ...style };
                          if (!newStyle.advanced) newStyle.advanced = {};
                          newStyle.advanced.inputFieldWidth = `${e.target.value}%`;
                          onChange(newStyle);
                        }}
                      />
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">20%</small>
                        <small className="text-muted">50%</small>
                        <small className="text-muted">100%</small>
                      </div>
                    </div>
                  )}
                  
                  <Form.Text className="text-muted">
                    자동: 내용에 맞게 자동 조정 | 수동: 차트 너비 대비 퍼센트로 설정
                  </Form.Text>
                </Form.Group>

                {/* 배치 방향 설정 */}
                <Form.Group className="mb-3">
                  <Form.Label>배치</Form.Label>
                  <Form.Select
                    value={style?.advanced?.orientation || 'vertical'}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.orientation = e.target.value;
                      onChange(newStyle);
                    }}
                  >
                    <option value="vertical">세로 (버튼 위, 입력 아래)</option>
                    <option value="horizontal">가로 (버튼-입력 일렬)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    레이아웃을 세로 또는 가로(일렬)로 배치합니다.
                  </Form.Text>
                </Form.Group>

                {/* 기간 Preset 버튼 표시 옵션 */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="date-picker-show-quick-buttons"
                    label="기간 선택 버튼 표시"
                    checked={style?.advanced?.showQuickButtons === true}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.showQuickButtons = e.target.checked;
                      if (!e.target.checked) {
                        // 버튼을 숨기면 개별 설정도 초기화
                        delete newStyle.advanced.presetButtons;
                      }
                      onChange(newStyle);
                    }}
                  />
                  <Form.Text className="text-muted">
                    오늘, 어제, 최근 7일 등의 빠른 선택 버튼을 표시합니다.
                  </Form.Text>
                </Form.Group>

                {/* 개별 Preset 버튼 토글 */}
                {style?.advanced?.showQuickButtons === true && (
                  <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>개별 버튼 표시 설정</strong>
                      </div>
                      <Row>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            id="preset-today"
                            label="오늘"
                            checked={style?.advanced?.presetButtons?.today !== false}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.options) newStyle.options = {};
                              if (!newStyle.advanced) newStyle.advanced = {};
                              if (!newStyle.advanced.presetButtons) newStyle.advanced.presetButtons = {};
                              newStyle.advanced.presetButtons.today = e.target.checked;
                              onChange(newStyle);
                            }}
                            className="mb-2"
                          />
                          <Form.Check
                            type="checkbox"
                            id="preset-yesterday"
                            label="어제"
                            checked={style?.advanced?.presetButtons?.yesterday !== false}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.options) newStyle.options = {};
                              if (!newStyle.advanced) newStyle.advanced = {};
                              if (!newStyle.advanced.presetButtons) newStyle.advanced.presetButtons = {};
                              newStyle.advanced.presetButtons.yesterday = e.target.checked;
                              onChange(newStyle);
                            }}
                            className="mb-2"
                          />
                          <Form.Check
                            type="checkbox"
                            id="preset-last7days"
                            label="최근 7일"
                            checked={style?.advanced?.presetButtons?.last7Days !== false}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.options) newStyle.options = {};
                              if (!newStyle.advanced) newStyle.advanced = {};
                              if (!newStyle.advanced.presetButtons) newStyle.advanced.presetButtons = {};
                              newStyle.advanced.presetButtons.last7Days = e.target.checked;
                              onChange(newStyle);
                            }}
                            className="mb-2"
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            id="preset-last30days"
                            label="최근 30일"
                            checked={style?.advanced?.presetButtons?.last30Days !== false}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.options) newStyle.options = {};
                              if (!newStyle.advanced) newStyle.advanced = {};
                              if (!newStyle.advanced.presetButtons) newStyle.advanced.presetButtons = {};
                              newStyle.advanced.presetButtons.last30Days = e.target.checked;
                              onChange(newStyle);
                            }}
                            className="mb-2"
                          />
                          <Form.Check
                            type="checkbox"
                            id="preset-thismonth"
                            label="이번 달"
                            checked={style?.advanced?.presetButtons?.thisMonth !== false}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.options) newStyle.options = {};
                              if (!newStyle.advanced) newStyle.advanced = {};
                              if (!newStyle.advanced.presetButtons) newStyle.advanced.presetButtons = {};
                              newStyle.advanced.presetButtons.thisMonth = e.target.checked;
                              onChange(newStyle);
                            }}
                            className="mb-2"
                          />
                          <Form.Check
                            type="checkbox"
                            id="preset-thisyear"
                            label="올해"
                            checked={style?.advanced?.presetButtons?.thisYear !== false}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.options) newStyle.options = {};
                              if (!newStyle.advanced) newStyle.advanced = {};
                              if (!newStyle.advanced.presetButtons) newStyle.advanced.presetButtons = {};
                              newStyle.advanced.presetButtons.thisYear = e.target.checked;
                              onChange(newStyle);
                            }}
                            className="mb-2"
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* 날짜 형식 설정 */}
                <Form.Group className="mb-3">
                  <Form.Label>날짜 표시 형식</Form.Label>
                  <Form.Select
                    value={style?.advanced?.dateFormat || 'yyyy-MM-dd'}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.dateFormat = e.target.value;
                      onChange(newStyle);
                    }}
                  >
                    <option value="yyyy-MM-dd">2024-01-01</option>
                    <option value="yyyy/MM/dd">2024/01/01</option>
                    <option value="dd-MM-yyyy">01-01-2024</option>
                    <option value="dd/MM/yyyy">01/01/2024</option>
                    <option value="MM-dd-yyyy">01-01-2024</option>
                    <option value="yyyy년 MM월 dd일">2024년 01월 01일</option>
                  </Form.Select>
                </Form.Group>

                {/* 선택 모드 */}
                <Form.Group className="mb-3">
                  <Form.Label>선택 모드</Form.Label>
                  <Form.Select
                    value={style?.advanced?.mode || 'range'}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.mode = e.target.value;
                      onChange(newStyle);
                    }}
                  >
                    <option value="point">시점 선택</option>
                    <option value="range">기간 선택</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    특정 시점 또는 기간 범위 선택 모드를 설정합니다.
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                {/* 시간 단위 선택 */}
                <Form.Group className="mb-3">
                  <Form.Label>시간 단위</Form.Label>
                  <Form.Select
                    value={style?.advanced?.unit || 'day'}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.unit = e.target.value;
                      onChange(newStyle);
                    }}
                  >
                    <option value="year">연도</option>
                    <option value="month">월</option>
                    <option value="day">일</option>
                    <option value="hour">시간</option>
                    <option value="minute">분</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    선택할 날짜/시간의 세밀도를 설정합니다.
                  </Form.Text>
                </Form.Group>

                {/* 초기값 프리셋 */}
                <Form.Group className="mb-3">
                  <Form.Label>초기값 프리셋</Form.Label>
                  <Form.Select
                    value={style?.advanced?.initialPreset || ''}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.initialPreset = e.target.value || undefined;
                      onChange(newStyle);
                    }}
                  >
                    <option value="">없음</option>
                    {(() => {
                      const unit = style?.advanced?.unit || 'day';
                      const mode = style?.advanced?.mode || 'range';
                      const options = [];
                      if ((unit === 'minute' || unit === 'hour') && mode === 'range') {
                        options.push(['last_15_min', '최근 15분']);
                        options.push(['last_1_hour', '최근 1시간']);
                        options.push(['last_6_hours', '최근 6시간']);
                        options.push(['last_24_hours', '최근 24시간']);
                      } else if ((unit === 'minute' || unit === 'hour') && mode === 'point') {
                        options.push(['now', '지금']);
                        options.push(['one_min_ago', '1분 전']);
                        options.push(['one_hour_ago', '1시간 전']);
                      } else if (unit === 'day' && mode === 'range') {
                        options.push(['today', '오늘']);
                        options.push(['yesterday', '어제']);
                        options.push(['last_7_days', '최근 7일']);
                        options.push(['last_30_days', '최근 30일']);
                        options.push(['this_month', '이번 달']);
                        options.push(['this_year', '올해']);
                      } else if (unit === 'day' && mode === 'point') {
                        // '없음'이 자동으로 오늘(now)로 동작하도록 하여 중복 제거
                        options.push(['yesterday', '어제']);
                      } else if (unit === 'month' && mode === 'range') {
                        options.push(['this_month', '이번 달']);
                        options.push(['last_month', '지난 달']);
                      } else if (unit === 'month' && mode === 'point') {
                        // '없음'이 자동으로 이번 달(현재 월)로 동작하므로 중복 제거
                        options.push(['last_month', '지난 달']);
                      } else if (unit === 'year' && mode === 'range') {
                        options.push(['this_year', '올해']);
                        options.push(['last_year', '작년']);
                      } else if (unit === 'year' && mode === 'point') {
                        // '없음'이 자동으로 올해로 동작하므로 중복 제거
                        options.push(['last_year', '작년']);
                      }
                      return options.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ));
                    })()}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    defaultValue가 설정되어 있으면 프리셋보다 우선 적용됩니다. '없음' 선택 시 point 모드에서는 현재 단위의 현재 시점이 초기값으로 사용됩니다(일/시간/분: 지금, 월: 이번 달, 연: 올해). range 모드에서는 빈 값으로 시작합니다.
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                {/* 레이아웃 방향 */}
                <Form.Group className="mb-3">
                  <Form.Label>레이아웃 방향</Form.Label>
                  <Form.Select
                    value={style?.advanced?.orientation || 'vertical'}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.orientation = e.target.value;
                      onChange(newStyle);
                    }}
                  >
                    <option value="vertical">세로 배치</option>
                    <option value="horizontal">가로 배치</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    버튼과 입력 필드의 배치 방향을 설정합니다.
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={12}>
                {/* Preset 버튼 표시 여부 */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="date-picker-show-presets"
                    label="빠른 선택 버튼 표시"
                    checked={style?.advanced?.showPresets !== false}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.showPresets = e.target.checked;
                      onChange(newStyle);
                    }}
                  />
                  <Form.Text className="text-muted">
                    오늘, 어제, 최근 7일 등의 빠른 선택 버튼을 표시합니다. (일 단위에서만 작동)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      {/* 테이블 전용 스타일 토글 섹션 */}
      {chartType === 'table' && (
        <Card className="mb-3">
          <Card.Header>테이블 표시 옵션</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="table-show-header"
                  label="헤더 표시"
                  checked={style?.advanced?.tableOptions?.showHeader !== false}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.showHeader = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="table-show-row-borders"
                  label="행 구분선"
                  checked={style?.advanced?.tableOptions?.showRowBorders !== false}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.showRowBorders = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="table-show-column-borders"
                  label="열 구분선"
                  checked={style?.advanced?.tableOptions?.showColumnBorders !== false}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.showColumnBorders = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="table-show-outer-border"
                  label="외곽 테두리"
                  checked={style?.advanced?.tableOptions?.showOuterBorder !== false}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.showOuterBorder = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="table-striped-rows"
                  label="줄무늬 효과"
                  checked={style?.advanced?.tableOptions?.stripedRows !== false}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.stripedRows = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="table-hover-effect"
                  label="호버 효과"
                  checked={style?.advanced?.tableOptions?.hoverEffect !== false}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.hoverEffect = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="table-compact-mode"
                  label="컴팩트 모드"
                  checked={style?.advanced?.tableOptions?.compactMode === true}
                  onChange={(e) => {
                    const newStyle = { ...style };
                    if (!newStyle.advanced) newStyle.advanced = {};
                    if (!newStyle.advanced.tableOptions) newStyle.advanced.tableOptions = {};
                    newStyle.advanced.tableOptions.compactMode = e.target.checked;
                    onChange(newStyle);
                  }}
                  className="mb-2"
                />
              </Col>
            </Row>
            <Form.Text className="text-muted d-block mt-2">
              테이블의 시각적 요소를 켜거나 끌 수 있습니다. 기본값은 모두 켜짐 상태입니다.
            </Form.Text>
          </Card.Body>
        </Card>
      )}

      {/* 테이블 배경색 설정 섹션 */}
      {chartType === 'table' && (
        <Card className="mb-3">
          <Card.Header>테이블 배경색</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>헤더 배경색</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={style?.tableColors?.header || style?.headerBackground || '#f8f9fa'}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.header = e.target.value;
                        onChange(newStyle);
                      }}
                      style={{ width: '60px' }}
                    />
                    <Form.Control
                      type="text"
                      value={style?.tableColors?.header || style?.headerBackground || ''}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.header = e.target.value;
                        onChange(newStyle);
                      }}
                      placeholder="#f8f9fa"
                    />
                  </div>
                  <Form.Text className="text-muted">테이블 헤더의 배경색입니다.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>행 헤더(첫 열) 배경색</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={style?.tableColors?.rowHeader || ''}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.rowHeader = e.target.value;
                        onChange(newStyle);
                      }}
                      style={{ width: '60px' }}
                    />
                    <Form.Control
                      type="text"
                      value={style?.tableColors?.rowHeader || ''}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.rowHeader = e.target.value;
                        onChange(newStyle);
                      }}
                      placeholder="-"
                    />
                  </div>
                  <Form.Text className="text-muted">행 헤더(첫 번째 열)가 있을 경우 배경을 별도 지정합니다.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>행 배경색</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={style?.tableColors?.row || style?.rowBackground || '#ffffff'}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.row = e.target.value;
                        onChange(newStyle);
                      }}
                      style={{ width: '60px' }}
                    />
                    <Form.Control
                      type="text"
                      value={style?.tableColors?.row || style?.rowBackground || ''}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.row = e.target.value;
                        onChange(newStyle);
                      }}
                      placeholder="#ffffff"
                    />
                  </div>
                  <Form.Text className="text-muted">기본 행의 배경색입니다.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>대체 행 배경색(줄무늬)</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={style?.tableColors?.rowAlternate || style?.rowAlternateBackground || 'rgba(248, 249, 250, 0.5)'}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.rowAlternate = e.target.value;
                        onChange(newStyle);
                      }}
                      style={{ width: '60px' }}
                    />
                    <Form.Control
                      type="text"
                      value={style?.tableColors?.rowAlternate || style?.rowAlternateBackground || ''}
                      onChange={(e) => {
                        const newStyle = { ...style };
                        if (!newStyle.tableColors) newStyle.tableColors = {};
                        newStyle.tableColors.rowAlternate = e.target.value;
                        onChange(newStyle);
                      }}
                      placeholder="rgba(248, 249, 250, 0.5)"
                    />
                  </div>
                  <Form.Text className="text-muted">줄무늬 행(짝수/홀수)에 적용되는 대체 배경색입니다.</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* 숫자 형식 설정 섹션 - 배지, 텍스트라벨, 테이블용 */}
      {['badge', 'text_label', 'table'].includes(chartType) && (
        <Card className="mb-3">
          <Card.Header>숫자 형식</Card.Header>
          <Card.Body>
            {/* 배지와 텍스트라벨용 단일 설정 */}
            {['badge', 'text_label'].includes(chartType) && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>소수점 자리수</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Select
                        value={style?.advanced?.decimalPlaces === null || style?.advanced?.decimalPlaces === undefined ? 'auto' : 'fixed'}
                        onChange={(e) => {
                          const newStyle = { ...style };
                          if (!newStyle.advanced) newStyle.advanced = {};
                          if (e.target.value === 'auto') {
                            newStyle.advanced.decimalPlaces = null;
                          } else {
                            newStyle.advanced.decimalPlaces = 0; // 기본값 0
                          }
                          onChange(newStyle);
                        }}
                        style={{ width: '150px' }}
                      >
                        <option value="auto">자동</option>
                        <option value="fixed">고정</option>
                      </Form.Select>
                      {style?.advanced?.decimalPlaces !== null && style?.advanced?.decimalPlaces !== undefined && (
                        <Form.Control
                          type="number"
                          value={style?.advanced?.decimalPlaces}
                          onChange={(e) => {
                            const newStyle = { ...style };
                            if (!newStyle.advanced) newStyle.advanced = {};
                            const value = parseInt(e.target.value);
                            if (!isNaN(value) && value >= 0 && value <= 10) {
                              newStyle.advanced.decimalPlaces = value;
                              onChange(newStyle);
                            }
                          }}
                          min={0}
                          max={10}
                          style={{ width: '100px' }}
                        />
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      자동: 정수는 그대로, 소수는 필요한 만큼만 표시<br/>
                      고정: 지정한 자리수로 항상 표시 (0~10)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* 테이블용 컬럼별 설정 */}
            {chartType === 'table' && (
              <>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>기본 소수점 자리수 (모든 컬럼)</Form.Label>
                      <div className="d-flex align-items-center gap-3">
                        <Form.Select
                          value={style?.advanced?.defaultDecimalPlaces === null || style?.advanced?.defaultDecimalPlaces === undefined ? 'auto' : 'fixed'}
                          onChange={(e) => {
                            const newStyle = { ...style };
                            if (!newStyle.advanced) newStyle.advanced = {};
                            if (e.target.value === 'auto') {
                              newStyle.advanced.defaultDecimalPlaces = null;
                            } else {
                              newStyle.advanced.defaultDecimalPlaces = 0;
                            }
                            onChange(newStyle);
                          }}
                          style={{ width: '150px' }}
                        >
                          <option value="auto">자동</option>
                          <option value="fixed">고정</option>
                        </Form.Select>
                        {style?.advanced?.defaultDecimalPlaces !== null && style?.advanced?.defaultDecimalPlaces !== undefined && (
                          <Form.Control
                            type="number"
                            value={style?.advanced?.defaultDecimalPlaces}
                            onChange={(e) => {
                              const newStyle = { ...style };
                              if (!newStyle.advanced) newStyle.advanced = {};
                              const value = parseInt(e.target.value);
                              if (!isNaN(value) && value >= 0 && value <= 10) {
                                newStyle.advanced.defaultDecimalPlaces = value;
                                onChange(newStyle);
                              }
                            }}
                            min={0}
                            max={10}
                            style={{ width: '100px' }}
                          />
                        )}
                      </div>
                      <Form.Text className="text-muted">
                        모든 숫자 컬럼에 적용될 기본 소수점 자리수
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* 컬럼별 개별 설정 (추후 확장 가능) */}
                <Row>
                  <Col md={12}>
                    <Form.Text className="text-muted">
                      <i className="bi bi-info-circle"></i> 컬럼별 개별 설정은 추후 지원 예정입니다.
                    </Form.Text>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* 미리보기 (선택적) */}
      {showPreview && selectedTarget && (
        <Card className="mt-3">
          <Card.Header>미리보기</Card.Header>
          <Card.Body>
            <div 
              style={{
                fontFamily: fontSettings.fontFamily,
                fontSize: `${fontSettings.fontSize}px`,
                fontWeight: fontSettings.fontWeight,
                fontStyle: fontSettings.fontStyle,
                color: fontSettings.color,
                lineHeight: fontSettings.lineHeight,
                padding: '15px',
                border: '1px solid #dee2e6'
              }}
            >
              <div>{getTargetLabel(selectedTarget, chartType)} 스타일 미리보기</div>
              <div style={{ fontSize: `${fontSettings.fontSize * 0.8}px` }}>
                현재 편집 중인 스타일이 적용된 모습입니다.
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* 배터리 그리드 전용 레이아웃 옵션 */}
      {chartType === 'battery_grid' && (
        <Card className="mb-3 mt-3">
          <Card.Header>배터리 그리드 레이아웃</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>열 개수 (columns)</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="number"
                      min={1}
                      max={50}
                      step={1}
                      value={(() => {
                        const v = style?.advanced?.gridColumns;
                        return (v === undefined || v === null) ? 6 : v;
                      })()}
                      onChange={(e) => {
                        const num = Math.max(1, Math.min(50, parseInt(e.target.value) || 1));
                        const newStyle = { ...style };
                        if (!newStyle.advanced) newStyle.advanced = {};
                        newStyle.advanced.gridColumns = num;
                        onChange(newStyle);
                      }}
                      placeholder="예: 6"
                    />
                  </div>
                  <Form.Text className="text-muted">
                    한 줄에 표시할 배터리 셀의 개수입니다. 컨테이너 크기가 변해도 열 개수는 유지됩니다. (기본값: 6)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>셀 세로 비율 (%)</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="number"
                      min={50}
                      max={300}
                      step={10}
                      value={(() => {
                        const v = style?.advanced?.cellHeightRatio;
                        return (v === undefined || v === null) ? 120 : v;
                      })()}
                      onChange={(e) => {
                        const num = Math.max(50, Math.min(300, parseInt(e.target.value) || 120));
                        const newStyle = { ...style };
                        if (!newStyle.advanced) newStyle.advanced = {};
                        newStyle.advanced.cellHeightRatio = num;
                        onChange(newStyle);
                      }}
                      placeholder="120"
                    />
                    <span className="text-muted">%</span>
                  </div>
                  <Form.Text className="text-muted">
                    셀의 세로 길이를 가로 길이 대비 비율로 지정합니다. (기본값: 120% = 가로의 1.2배)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>표시 모드</Form.Label>
                  <Form.Select
                    value={(() => {
                      const v = style?.advanced?.cellType;
                      return (v === undefined || v === null) ? 1 : v;
                    })()}
                    onChange={(e) => {
                      const num = parseInt(e.target.value) || 1;
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.cellType = num;
                      onChange(newStyle);
                    }}
                  >
                    <option value={1}>배터리 아이콘 + 내부 SOC + SOH</option>
                    <option value={2}>가로 게이지 + SOC (아래)</option>
                    <option value={3}>가로 게이지(내부 SOC) + SOH (아래)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    배터리 셀의 표현 방식을 선택합니다.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>마지막 줄 정렬</Form.Label>
                  <Form.Select
                    value={(style?.advanced?.lastRowAlignment || 'center')}
                    onChange={(e) => {
                      const newStyle = { ...style };
                      if (!newStyle.advanced) newStyle.advanced = {};
                      newStyle.advanced.lastRowAlignment = e.target.value;
                      onChange(newStyle);
                    }}
                  >
                    <option value="left">왼쪽 정렬</option>
                    <option value="center">가운데 정렬</option>
                    <option value="right">오른쪽 정렬</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    마지막 줄의 셀 개수가 차트 폭을 가득 채우지 못할 때의 정렬 방식입니다.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* 배터리 그리드 레전드 설정 */}
      {chartType === 'battery_grid' && (
        <BatteryGridLegendEditor 
          style={style} 
          onChange={onChange} 
        />
      )}
    </div>
  );
};

/**
 * 배터리 그리드 레전드 편집 컴포넌트
 * 레전드 설정이 없으면 빈 상태로 시작 (기본값 없음)
 * 
 * 범용적인 용어 사용:
 * - cartStatus → 셀 배경색 (값에 따른 배경색 매핑)
 * - soc → 게이지 색상 (범위에 따른 게이지 색상)
 * - state → 테두리/배경 강조 (값에 따른 테두리 및 배경 오버라이드)
 */
const BatteryGridLegendEditor = ({ style, onChange }) => {
  // 현재 레전드 설정 가져오기 (없으면 빈 객체)
  const getLegendConfig = useCallback(() => {
    return style?.legend || {};
  }, [style]);

  // 레전드 설정 업데이트 (title 유지)
  const updateLegend = useCallback((category, updates) => {
    const newStyle = { ...style };
    if (!newStyle.legend) newStyle.legend = {};
    // 기존 설정과 병합 (title 유지)
    newStyle.legend[category] = {
      ...newStyle.legend[category],
      ...updates
    };
    onChange(newStyle);
  }, [style, onChange]);

  // 레전드 제목 업데이트
  const updateLegendTitle = useCallback((category, title) => {
    const newStyle = { ...style };
    if (!newStyle.legend) newStyle.legend = {};
    if (!newStyle.legend[category]) newStyle.legend[category] = {};
    newStyle.legend[category].title = title;
    onChange(newStyle);
  }, [style, onChange]);

  // 셀 배경색 항목 업데이트
  const updateCartStatusItem = useCallback((index, field, value) => {
    const legend = getLegendConfig();
    const items = [...(legend.cartStatus?.items || [])];
    items[index] = { ...items[index], [field]: value };
    updateLegend('cartStatus', { items });
  }, [getLegendConfig, updateLegend]);

  // 셀 배경색 항목 추가
  const addCartStatusItem = useCallback(() => {
    const legend = getLegendConfig();
    const items = [...(legend.cartStatus?.items || [])];
    const maxValue = items.length > 0 ? Math.max(...items.map(i => i.value)) : -1;
    items.push({ value: maxValue + 1, label: `항목 ${maxValue + 1}`, color: '#CCCCCC' });
    updateLegend('cartStatus', { items });
  }, [getLegendConfig, updateLegend]);

  // 셀 배경색 항목 삭제
  const removeCartStatusItem = useCallback((index) => {
    const legend = getLegendConfig();
    const items = [...(legend.cartStatus?.items || [])];
    items.splice(index, 1);
    updateLegend('cartStatus', { items });
  }, [getLegendConfig, updateLegend]);

  // 게이지 색상 항목 업데이트
  const updateSocItem = useCallback((index, field, value) => {
    const legend = getLegendConfig();
    const items = [...(legend.soc?.items || [])];
    items[index] = { ...items[index], [field]: field === 'min' || field === 'max' ? parseInt(value) || 0 : value };
    updateLegend('soc', { type: 'range', items });
  }, [getLegendConfig, updateLegend]);

  // 게이지 색상 항목 추가
  const addSocItem = useCallback(() => {
    const legend = getLegendConfig();
    const items = [...(legend.soc?.items || [])];
    items.push({ min: 0, max: 0, label: '새 범위', color: '#CCCCCC' });
    updateLegend('soc', { type: 'range', items });
  }, [getLegendConfig, updateLegend]);

  // 게이지 색상 항목 삭제
  const removeSocItem = useCallback((index) => {
    const legend = getLegendConfig();
    const items = [...(legend.soc?.items || [])];
    items.splice(index, 1);
    updateLegend('soc', { type: 'range', items });
  }, [getLegendConfig, updateLegend]);

  // 테두리/배경 항목 업데이트
  const updateStateItem = useCallback((index, field, value) => {
    const legend = getLegendConfig();
    const items = [...(legend.state?.items || [])];
    items[index] = { ...items[index], [field]: value };
    updateLegend('state', { items });
  }, [getLegendConfig, updateLegend]);

  // 테두리/배경 항목 추가
  const addStateItem = useCallback(() => {
    const legend = getLegendConfig();
    const items = [...(legend.state?.items || [])];
    const maxValue = items.length > 0 ? Math.max(...items.map(i => i.value)) : -1;
    items.push({ value: maxValue + 1, label: `항목 ${maxValue + 1}`, border: '1px solid #CCCCCC', background: null });
    updateLegend('state', { items });
  }, [getLegendConfig, updateLegend]);

  // 테두리/배경 항목 삭제
  const removeStateItem = useCallback((index) => {
    const legend = getLegendConfig();
    const items = [...(legend.state?.items || [])];
    items.splice(index, 1);
    updateLegend('state', { items });
  }, [getLegendConfig, updateLegend]);

  // 모든 레전드 설정 삭제
  const clearAllLegend = useCallback(() => {
    const newStyle = { ...style };
    delete newStyle.legend;
    onChange(newStyle);
  }, [style, onChange]);

  const legend = getLegendConfig();
  const cartStatusItems = legend.cartStatus?.items || [];
  const socItems = legend.soc?.items || [];
  const stateItems = legend.state?.items || [];
  const hasAnyLegend = cartStatusItems.length > 0 || socItems.length > 0 || stateItems.length > 0;

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>배터리 그리드 레전드 설정</span>
        {hasAnyLegend && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={clearAllLegend}
          >
            모든 설정 삭제
          </button>
        )}
      </Card.Header>
      <Card.Body>
        {/* 안내 메시지 */}
        {!hasAnyLegend && (
          <div className="alert alert-secondary mb-3" style={{ fontSize: '0.85rem' }}>
            <i className="bi bi-info-circle"></i>{' '}
            레전드 설정이 없습니다. 아래에서 항목을 추가하면 차트에 범례가 표시됩니다.
            설정이 없으면 기본 색상과 값이 그대로 표시됩니다.
          </div>
        )}

        {/* 셀 배경색 설정 */}
        <div className="mb-4">
          <h6 className="mb-3" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
            📦 셀 배경색
          </h6>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted mb-1">레전드 제목 (범례에 표시)</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder="예: 운영상태, 카트상태 등"
              value={legend.cartStatus?.title || ''}
              onChange={(e) => updateLegendTitle('cartStatus', e.target.value)}
              style={{ maxWidth: '250px' }}
            />
          </Form.Group>
          {cartStatusItems.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>값</th>
                  <th>라벨</th>
                  <th style={{ width: '120px' }}>배경색</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {cartStatusItems.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={item.value}
                        onChange={(e) => updateCartStatusItem(index, 'value', parseInt(e.target.value) || 0)}
                        style={{ width: '60px' }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={item.label}
                        onChange={(e) => updateCartStatusItem(index, 'label', e.target.value)}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          type="color"
                          size="sm"
                          value={item.color || '#CCCCCC'}
                          onChange={(e) => updateCartStatusItem(index, 'color', e.target.value)}
                          style={{ width: '40px', height: '30px', padding: '2px' }}
                        />
                        <Form.Control
                          type="text"
                          size="sm"
                          value={item.color || ''}
                          onChange={(e) => updateCartStatusItem(index, 'color', e.target.value)}
                          style={{ width: '80px', fontSize: '11px' }}
                        />
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeCartStatusItem(index)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
            <div className="text-muted small mb-2">설정된 항목이 없습니다.</div>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addCartStatusItem}
          >
            + 항목 추가
          </button>
        </div>

        {/* 게이지 색상 설정 */}
        <div className="mb-4">
          <h6 className="mb-3" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
            📊 게이지 색상
          </h6>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted mb-1">레전드 제목 (범례에 표시)</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder="예: SOC, 충전량, 배터리잔량 등"
              value={legend.soc?.title || ''}
              onChange={(e) => updateLegendTitle('soc', e.target.value)}
              style={{ maxWidth: '250px' }}
            />
          </Form.Group>
          {socItems.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>최소</th>
                  <th style={{ width: '80px' }}>최대</th>
                  <th>라벨</th>
                  <th style={{ width: '120px' }}>색상</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {socItems.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={item.min}
                        onChange={(e) => updateSocItem(index, 'min', e.target.value)}
                        style={{ width: '60px' }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={item.max}
                        onChange={(e) => updateSocItem(index, 'max', e.target.value)}
                        style={{ width: '60px' }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={item.label}
                        onChange={(e) => updateSocItem(index, 'label', e.target.value)}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          type="color"
                          size="sm"
                          value={item.color || '#CCCCCC'}
                          onChange={(e) => updateSocItem(index, 'color', e.target.value)}
                          style={{ width: '40px', height: '30px', padding: '2px' }}
                        />
                        <Form.Control
                          type="text"
                          size="sm"
                          value={item.color || ''}
                          onChange={(e) => updateSocItem(index, 'color', e.target.value)}
                          style={{ width: '80px', fontSize: '11px' }}
                        />
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeSocItem(index)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
            <div className="text-muted small mb-2">설정된 항목이 없습니다. 설정이 없으면 값에 따른 기본 그라데이션 색상이 사용됩니다.</div>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addSocItem}
          >
            + 항목 추가
          </button>
        </div>

        {/* 테두리/배경 강조 설정 */}
        <div className="mb-2">
          <h6 className="mb-3" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
            🔲 테두리/배경 강조
          </h6>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted mb-1">레전드 제목 (범례에 표시)</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder="예: 상태, 경고단계, 알림 등"
              value={legend.state?.title || ''}
              onChange={(e) => updateLegendTitle('state', e.target.value)}
              style={{ maxWidth: '250px' }}
            />
          </Form.Group>
          {stateItems.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '60px' }}>값</th>
                  <th>라벨</th>
                  <th style={{ width: '150px' }}>테두리</th>
                  <th style={{ width: '120px' }}>배경색</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {stateItems.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={item.value}
                        onChange={(e) => updateStateItem(index, 'value', parseInt(e.target.value) || 0)}
                        style={{ width: '50px' }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={item.label}
                        onChange={(e) => updateStateItem(index, 'label', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={item.border || ''}
                        onChange={(e) => updateStateItem(index, 'border', e.target.value)}
                        placeholder="예: 3px solid #F44336"
                        style={{ fontSize: '11px' }}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          type="color"
                          size="sm"
                          value={item.background || '#FFFFFF'}
                          onChange={(e) => updateStateItem(index, 'background', e.target.value)}
                          style={{ width: '40px', height: '30px', padding: '2px' }}
                          disabled={!item.background}
                        />
                        <Form.Control
                          type="text"
                          size="sm"
                          value={item.background || ''}
                          onChange={(e) => updateStateItem(index, 'background', e.target.value || null)}
                          placeholder="없음"
                          style={{ width: '70px', fontSize: '11px' }}
                        />
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeStateItem(index)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
            <div className="text-muted small mb-2">설정된 항목이 없습니다. 설정이 없으면 기본 테두리만 표시됩니다.</div>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addStateItem}
          >
            + 항목 추가
          </button>
        </div>

        {hasAnyLegend && (
        <div className="alert alert-info mt-3" style={{ fontSize: '0.85rem' }}>
          <i className="bi bi-info-circle"></i>{' '}
          <strong>안내:</strong> 레전드 설정이 저장되면 차트의 범례와 색상 매핑에 반영됩니다.
          레전드 제목을 입력하면 해당 범례가 차트에 표시됩니다.
        </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default CommonStylePanel;